import React, { useState } from 'react';
import { Tag, Sparkles, Server, Check, ShieldCheck, Mail, Phone, Lock, RefreshCw } from 'lucide-react';
import { CustomProject } from '../../types/productBuilder';

interface PublishProps {
  project: CustomProject;
  onUpdateProject: (p: CustomProject) => void;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
}

export const Publish: React.FC<PublishProps> = ({ project, onUpdateProject, onAddLog }) => {
  const [compiling, setCompiling] = useState(false);
  const [compileProgress, setCompileProgress] = useState(0);
  const [published, setPublished] = useState(false);

  const isApplied = (project as any).bundleApplied === true;

  const handlePublish = () => {
    setCompiling(true);
    setCompileProgress(0);
    onAddLog('Product Builder', `Preparing production build configuration for "${project.name}"`, 'SUCCESS', 'Production Build Engine diagnostics initialized.');

    let p = 0;
    const interval = setInterval(() => {
      p += 20;
      setCompileProgress(p);
      if (p >= 100) {
        clearInterval(interval);
        setCompiling(false);
        setPublished(true);
        onAddLog('Product Builder', 'Production build configuration successfully evaluated. Standard static build draft initialized in sandbox.', 'SUCCESS', 'Static pages verified.');
      }
    }, 400);
  };

  const applyBundle = () => {
    onUpdateProject({
      ...project,
      bundleApplied: true
    } as any);

    onAddLog('Product Builder', 'Locked Connected Launch Bundle during Publish step', 'SUCCESS', 'Pricing discount recalculated.');
    alert("✨ DISCOUNTS LOCKED IN!\n\nStandard setup fee has been set to £239.00 (one-off promotional setup/introductory rate). Managed domain/email configurations have been registered in staging draft.");
  };

  return (
    <div className="npx-builder-publish-container space-y-6 text-left animate-fade-in text-zinc-400">
      <div className="border-b border-white/5 pb-4 font-mono">
        <div className="flex items-center gap-1.5 font-bold">
          <Server className="w-4 h-4 text-[#00ffff]" />
          <h2 className="text-sm font-mono tracking-widest text-[#00ffff] uppercase font-black">10. Deployment Readiness Preview</h2>
        </div>
        <p className="text-xs text-zinc-400 mt-1 font-sans">Review your draft layout structure and prepare your production build requirements. Live deployment requires official administrative approval.</p>
      </div>

      {!published ? (
        <div className="py-12 text-center max-w-md mx-auto space-y-6">
          <div className="space-y-2">
            <h3 className="text-md font-bold font-mono text-white uppercase tracking-wider">Evaluate Deployment Readiness?</h3>
            <p className="text-xs text-zinc-400 font-sans leading-relaxed">
              This runs a trial production build simulation to evaluate custom pages, styles, configurations, and scripts for build completeness within the safe sandbox environment.
            </p>
          </div>

          {compiling ? (
            <div className="space-y-3 font-mono">
              <div className="flex justify-between items-center text-[10px] text-[#00ffff]">
                <span>PRODUCTION BUILD CHECK IN PROGRESS...</span>
                <span>{compileProgress}%</span>
              </div>
              <div className="w-full bg-[#0a0f28] h-1.5 rounded-full overflow-hidden">
                <div className="bg-[#00ffff] h-full transition-all duration-300" style={{ width: `${compileProgress}%` }} />
              </div>
            </div>
          ) : (
            <button
              onClick={handlePublish}
              className="px-6 py-3 bg-gradient-to-r from-cyan-900 to-purple-950 hover:from-[#00ffff] hover:to-purple-500 hover:text-black border border-[#00ffff]/20 hover:border-transparent font-mono text-[10.5px] font-black uppercase text-white rounded-xl shadow-[0_0_20px_rgba(0,255,255,0.1)] transition-all cursor-pointer flex items-center gap-1.5 mx-auto"
            >
              <Sparkles className="w-4 h-4" />
              <span>Run Production Build Evaluation</span>
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6 animate-fade-in font-sans">
          
          {/* PUBLISHED SUCCESS PLATFORM DETAILS */}
          <div className="p-5 rounded-2xl bg-black/60 border border-[#00ffff]/20 text-left font-mono space-y-4">
            <div className="flex items-center gap-2 text-cyan-400 border-b border-white/5 pb-3">
              <ShieldCheck className="w-5 h-5 animate-pulse" />
              <h3 className="text-sm font-black uppercase text-white tracking-widest">Evaluation Complete — Draft Ready</h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs pt-1.5">
              <div className="space-y-1.5">
                <span className="text-[8px] text-zinc-500 block uppercase font-bold leading-none">Preview Staging Target:</span>
                <span className="text-[10px] font-bold text-yellow-500 uppercase break-all font-mono text-zinc-400">Preview Entry Staged (Pending Administrative Review)</span>
                <span className="text-[8px] text-zinc-500 block uppercase font-bold leading-none pt-2">Diagnostic Build Report:</span>
                <span className="text-[10px] text-zinc-400">Trial compiler completed successfully. All modular components parsed for build readiness.</span>
              </div>

              <div className="space-y-1.5 border-t md:border-t-0 md:border-l border-white/5 pt-3 md:pt-0 md:pl-4 col-span-1 text-left">
                <span className="text-[8px] text-zinc-500 block uppercase font-bold leading-none">Deployment Action Required:</span>
                <span className="text-[10.5px] text-amber-500 font-extrabold flex items-center gap-1 font-mono">
                  <span>ADMIN APPROVAL REQUIRED FOR PUBLISHING</span>
                </span>
                <span className="text-[8px] text-zinc-500 block uppercase font-bold leading-none pt-2 font-sans font-black uppercase">Release status:</span>
                <span className="text-[10px] text-white">Staged draft is locked within isolated preview. Pending final administrative workspace binding.</span>
              </div>
            </div>
          </div>

          {/* INTRODUCTORY BUNDLE OFFER */}
          <div className="p-5 rounded-2xl bg-[#e5c158]/5 border border-[#e5c158]/20 flex flex-col md:flex-row items-center justify-between text-left font-mono gap-4.5">
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <Tag size={13} className="text-[#e5c158]" />
                <span className="bg-[#e5c158] text-black text-[7.5px] font-sans font-black uppercase px-2 py-0.5 rounded leading-none">
                  ONBOARDING SPECIAL
                </span>
                <h4 className="text-xs font-bold text-white uppercase tracking-wider">Connected Launch Rollout Setup</h4>
              </div>
              <p className="text-[10px] text-zinc-455 leading-relaxed font-sans normal-case max-w-md text-zinc-400">
                Standard setup fees for hosting, custom enterprise email, and domain setup is £399.00. Locking the Connected Launch Bundle sets your entry setup fee instantly to £239.00. No hidden penalty contracts.
              </p>
            </div>

            {isApplied ? (
              <div className="border border-[#00ff00]/20 bg-[#00ff00]/5 text-[#ffc526] font-extrabold text-[10px] uppercase rounded-xl px-4 py-2 flex items-center gap-1.5 shrink-0">
                <Check size={12} className="text-[#00ff00]" />
                <span>Bundle Discount Applied (£239.00 setup)</span>
              </div>
            ) : (
              <button
                onClick={applyBundle}
                className="px-4 py-2 bg-[#e5c158] hover:bg-[#e5c158]/85 text-black font-semibold rounded-xl text-[10px] font-mono uppercase font-black tracking-wider transition-colors cursor-pointer shrink-0"
              >
                Apply Bundle Lock (Save Setup Cost)
              </button>
            )}
          </div>

          {/* CONTACT METHOD LINKS */}
          <div className="p-4 bg-zinc-950/60 border border-white/5 rounded-2xl flex flex-wrap items-center justify-between gap-4 font-mono text-[10px] text-zinc-500">
            <div className="flex gap-2 items-center font-bold">
              <Phone size={12} className="text-[#00ffff] animate-bounce shrink-0" />
              <span className="text-white">NexoraX Support anchor: 08000 622827</span>
            </div>
            
            <div className="flex gap-2 items-center">
              <Mail size={12} className="text-purple-400 shrink-0" />
              <span>Correspondence Support: support@nexoraxdigital.co.uk | info@nexoraxdigital.co.uk</span>
            </div>
          </div>

        </div>
      )}
    </div>
  );
};
